import { lightTheme } from "@yleisradio/yds-core";

import { SUBTITLES_BACKGROUND_COLOR_HEX } from "../../constants";
import { SUBTITLES_BACKGROUND } from "../../enums";
import { resolveTwoLetterCode } from "../../helpers/language";
import { loadAsset } from "../../helpers/scripts";
import { AudioTrack } from "../../types/common";
import { Subtitles } from "../../types/player-api";

interface Request extends chrome.cast.media.LoadRequest {
  credentials?: string;
  credentialsType?: string;
}

interface ITrack extends chrome.cast.media.Track {
  roles?: string[];
}

export const initializeCastApi = (onCastApiAvailable: () => void) => {
  if (window.chrome.cast) {
    return onCastApiAvailable();
  }

  window["__onGCastApiAvailable"] = function (isAvailable) {
    if (isAvailable) {
      onCastApiAvailable();
    }
  };
  loadAsset({
    url: "https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1"
  });
};

export const resolveActiveCastTracks = async (
  player: cast.framework.RemotePlayer,
  selectedAudioTrackId?: number,
  textTrack?: Subtitles
): Promise<void> => {
  const tracks: ITrack[] = player.mediaInfo?.tracks || [];

  const castSession =
    cast.framework.CastContext.getInstance().getCurrentSession();
  const media = castSession?.getMediaSession();

  const activeTrackIds: number[] = tracks.reduce(
    (acc, track) => {
      if (
        selectedAudioTrackId !== undefined &&
        track.trackId === selectedAudioTrackId
      ) {
        acc[0] = track.trackId;
      }
      if (
        track.type === chrome.cast.media.TrackType.TEXT &&
        track.language === textTrack?.language &&
        track.name.toLocaleLowerCase() === textTrack.label?.toLocaleLowerCase()
      ) {
        acc[1] = track.trackId;
      }
      return acc;
    },
    // When we don´t have more than one audio track. We still need to send something to chromecast
    // Our player handles those videos with null tracks.
    [1] as number[]
  );

  const tracksInfoRequest = new chrome.cast.media.EditTracksInfoRequest(
    activeTrackIds
  );

  return new Promise((resolve, reject) =>
    media?.editTracksInfo(tracksInfoRequest, resolve, reject)
  );
};

export const resolveTrackStyleChanges = async (
  subtitlesBackground: SUBTITLES_BACKGROUND,
  subtitlesSizeMultiplier: number
): Promise<void> => {
  const castSession =
    cast.framework.CastContext.getInstance().getCurrentSession();
  const media = castSession?.getMediaSession();

  const textTrackStyle = new chrome.cast.media.TextTrackStyle();
  textTrackStyle.backgroundColor =
    SUBTITLES_BACKGROUND_COLOR_HEX[subtitlesBackground];
  textTrackStyle.edgeColor =
    subtitlesBackground === SUBTITLES_BACKGROUND.NONE
      ? "#00000040"
      : "#000000FF";
  textTrackStyle.edgeType =
    subtitlesBackground === SUBTITLES_BACKGROUND.NONE
      ? chrome.cast.media.TextTrackEdgeType.OUTLINE
      : chrome.cast.media.TextTrackEdgeType.NONE;
  textTrackStyle.fontScale = subtitlesSizeMultiplier;
  textTrackStyle.foregroundColor =
    subtitlesBackground === SUBTITLES_BACKGROUND.WHITE
      ? lightTheme.TEXT_DEFAULT + "FF"
      : lightTheme.TEXT_NEGATIVE + "FF";

  const tracksInfoRequest = new chrome.cast.media.EditTracksInfoRequest(
    media?.activeTrackIds,
    textTrackStyle
  );
  media?.editTracksInfo(
    tracksInfoRequest,
    () => {
      /**no-op */
    },
    () => console.error("Failed to edit tracks info")
  );
};

const resolveSeekPosition = (
  seekPosition: number,
  seekableStart: number,
  seekableEnd: number
) => {
  if (seekPosition > seekableEnd) {
    return seekableEnd;
  } else if (seekPosition < seekableStart) {
    return seekableStart;
  } else {
    return seekPosition;
  }
};

export const seekFor = (
  amount: number,
  isLive: boolean,
  player: cast.framework.RemotePlayer,
  playerController: cast.framework.RemotePlayerController
) => {
  const seekPosition = player.currentTime + amount;
  if (isLive && player.liveSeekableRange) {
    const { end, start } = player.liveSeekableRange;
    if (end && start) {
      player.currentTime = resolveSeekPosition(seekPosition, start, end);
    }
  } else {
    player.currentTime = resolveSeekPosition(seekPosition, 0, player.duration);
  }
  playerController.seek();
};

export const getCastMediaInfo = (
  id: string,
  isLive: boolean
): chrome.cast.media.MediaInfo => {
  const mediaInfo = new chrome.cast.media.MediaInfo(
    id,
    // FIXME: Should be MIME type https://developer.mozilla.org/en-US/docs/Web/HTTP/MIME_types/Common_types
    isLive ? "LIVE" : "BUFFERED"
  );
  mediaInfo.streamType = isLive
    ? chrome.cast.media.StreamType.LIVE
    : chrome.cast.media.StreamType.BUFFERED;
  return mediaInfo;
};

export const getCastLoadRequest = ({
  consentCookie,
  currentTime,
  language,
  mediaInfo,
  token
}: {
  consentCookie: {
    userconsent?: string;
    yleconsent?: string;
  };
  currentTime?: number;
  language: string;
  mediaInfo: chrome.cast.media.MediaInfo;
  token: string;
  userconsent?: string;
}): chrome.cast.media.LoadRequest => {
  const request: Request = new chrome.cast.media.LoadRequest(mediaInfo);
  if (currentTime !== undefined) {
    request.currentTime = currentTime;
  }
  if (token) {
    request.credentials = token;
    request.credentialsType = "temporary";
  }
  if (language) {
    request.customData = {
      fallbackAudioLang: resolveTwoLetterCode(language)
    };
  }
  request.customData["consentCookie"] = consentCookie;

  return request;
};

const isSameTrack = (
  obj1: AudioTrack | Subtitles,
  obj2: AudioTrack | Subtitles
): boolean =>
  Object.keys(obj1).length === Object.keys(obj2).length &&
  Object.entries(obj1).every(([key, value]) => obj2[key] === value);

export const areSameTracks = (
  arr1: (AudioTrack | Subtitles)[],
  arr2: (AudioTrack | Subtitles)[]
): boolean =>
  arr1.length === arr2.length &&
  arr1.every((item, index) => isSameTrack(item, arr2[index]));
